import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BuildingsIcon, ReceiptIcon, ForkKnifeIcon, ArrowCounterClockwiseIcon, InfoIcon, CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { parseGSTIN } from "@/lib/gstin";

const OVERRIDABLE = [
  "gst_number", "restaurant_gst_number",
  "default_tax_rate", "default_tax_inclusive",
  "meal_plans", "extra_bed_rate", "cnb_rate",
  "invoice_prefix", "invoice_footer", "invoice_terms", "invoice_layout",
  "restaurant_name", "currency_symbol",
];

const INVOICE_LAYOUT_FIELDS = [
  ["show_property_gstin", "Property GSTIN"],
  ["show_property_address", "Property address"],
  ["show_property_contact", "Property phone & email"],
  ["show_logo", "Property logo"],
  ["show_booking_ref", "Booking reference"],
  ["show_stay_details", "Stay details"],
  ["show_guest_id", "Guest ID (KYC)"],
  ["show_guest_gstin_pan", "Guest GSTIN / PAN"],
  ["show_corporate_block", "Corporate bill-to block"],
  ["show_tax_breakdown", "Per-section GST breakdown"],
  ["show_deposit_line", "Deposit applied line"],
  ["show_balance_due", "Balance due line"],
  ["show_payment_reference", "Payment reference / UTR"],
  ["show_notes", "Notes section"],
  ["show_footer_signature", "Footer signature text"],
  ["show_computer_generated_line", '"Computer-generated" line'],
];

/**
 * @param {{ sections?: Array<'gst'|'invoice'|'meal_plans'> }} props
 *   Undefined `sections` renders every section. Passing a filter renders only
 *   those sections (used by the GST / Invoice / Meal Plans tabs so each has
 *   its own property picker without duplicating layout).
 */
export default function PropertyOverridesPanel({ sections }) {
  const showAll = !sections;
  const show = (name) => showAll || sections.includes(name);
  const [properties, setProperties] = useState([]);
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null); // { overrides, inherited, effective }
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProperties(r.data);
      if (r.data.length && !pid) setPid(r.data[0].id);
    });
  }, []);

  const load = () => {
    if (!pid) return;
    api.get(`/settings/property/${pid}`).then((r) => {
      setData(r.data);
      setDraft({ ...(r.data.overrides || {}) });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const inherited = data?.inherited || {};
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const clearField = (k) => setDraft((d) => { const n = { ...d }; delete n[k]; return n; });
  const isOverridden = (k) => data?.overrides && Object.prototype.hasOwnProperty.call(data.overrides, k);
  const isOverriddenFn = isOverridden;

  const activeProperty = useMemo(() => properties.find((p) => p.id === pid), [properties, pid]);

  const save = async () => {
    if (!pid) return;
    setSaving(true);
    try {
      // Build payload — send null for cleared fields so backend removes the override
      const payload = {};
      OVERRIDABLE.forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(draft, k)) {
          payload[k] = draft[k];
        } else if (isOverriddenFn(k)) {
          // was overridden, now cleared
          payload[k] = null;
        }
      });
      await api.put(`/settings/property/${pid}`, payload);
      toast.success(`Overrides saved for ${activeProperty?.name || "property"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!pid) return;
    if (!window.confirm(`Reset ALL overrides for ${activeProperty?.name}? The property will fall back to platform defaults.`)) return;
    setSaving(true);
    try {
      const payload = {};
      OVERRIDABLE.forEach((k) => { payload[k] = null; });
      await api.put(`/settings/property/${pid}`, payload);
      toast.success("Overrides cleared — property inherits platform defaults");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const InheritedNote = ({ k, formatter }) => (
    <p className="text-[10px] text-muted-foreground mt-1">
      Platform default: <span className="font-mono-plex">{formatter ? formatter(inherited[k]) : String(inherited[k] ?? "—")}</span>
    </p>
  );

  const OverrideBadge = ({ k }) => isOverridden(k) ? (
    <Badge variant="outline" className="text-[9px] bg-secondary/15 text-secondary border-secondary/30 uppercase tracking-widest" data-testid={`prop-override-badge-${k}`}>Overridden</Badge>
  ) : (
    <Badge variant="outline" className="text-[9px] bg-muted text-muted-foreground uppercase tracking-widest">Inherited</Badge>
  );

  const currentPlans = draft.meal_plans !== undefined ? draft.meal_plans : (data?.effective?.meal_plans || []);
  const updatePlanRow = (idx, key, value) => {
    const next = (currentPlans || []).map((p, i) => i === idx ? { ...p, [key]: value } : p);
    setField("meal_plans", next);
  };

  const titleFor = (() => {
    if (showAll) return "GST, Invoice, Meal Plans & Extras";
    if (sections.length === 1 && sections[0] === "gst") return "GST & tax · per property";
    if (sections.length === 1 && sections[0] === "invoice") return "Invoice template · per property";
    if (sections.length === 1 && sections[0] === "meal_plans") return "Meal Plans & extras · per property";
    return "Property overrides";
  })();

  return (
    <div className="space-y-6 max-w-4xl">
      <Card className="p-6">
        <div className="flex items-center gap-3">
          <BuildingsIcon size={22} weight="duotone" className="text-primary" />
          <div className="flex-1">
            <p className="label-overline">Per-property overrides</p>
            <h3 className="font-display text-2xl mt-0.5">{titleFor}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Each hotel can pin its own values. Anything you leave blank inherits from the platform default.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Property</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-72" data-testid="prop-overrides-picker"><SelectValue placeholder="Select property" /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}
            </SelectContent>
          </Select>
          {activeProperty && (
            <span className="text-xs text-muted-foreground">
              Editing overrides for <strong>{activeProperty.name}</strong>
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={resetAll} disabled={!pid || saving} className="ml-auto text-destructive hover:text-destructive" data-testid="prop-overrides-reset-all">
            <ArrowCounterClockwiseIcon size={14} className="mr-1" /> Reset to platform defaults
          </Button>
        </div>
      </Card>

      {data && (
        <>
          {/* GST section */}
          {show("gst") && (
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ReceiptIcon size={18} weight="duotone" className="text-primary" />
              <h4 className="font-display text-xl">GST & tax</h4>
            </div>

            <FieldRow label="GSTIN">
              <div className="flex items-center gap-2">
                <Input
                  value={draft.gst_number ?? data.effective.gst_number ?? ""}
                  onChange={(e) => setField("gst_number", e.target.value.toUpperCase())}
                  placeholder={inherited.gst_number || "e.g. 10AABCU9603R1ZM"}
                  maxLength={15}
                  className="font-mono-plex"
                  data-testid="prop-override-gst-number"
                />
                <OverrideBadge k="gst_number" />
                {isOverridden("gst_number") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("gst_number")}>reset</button>}
              </div>
              <GstinAutoParse value={draft.gst_number ?? data.effective.gst_number ?? ""} />
              <InheritedNote k="gst_number" />
            </FieldRow>

            <FieldRow label="Restaurant GSTIN (POS)">
              <div className="flex items-center gap-2">
                <Input
                  value={draft.restaurant_gst_number ?? data.effective.restaurant_gst_number ?? ""}
                  onChange={(e) => setField("restaurant_gst_number", e.target.value.toUpperCase())}
                  placeholder={inherited.restaurant_gst_number || "Leave blank to reuse hotel GSTIN"}
                  maxLength={15}
                  className="font-mono-plex"
                  data-testid="prop-override-restaurant-gst"
                />
                <OverrideBadge k="restaurant_gst_number" />
                {isOverridden("restaurant_gst_number") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("restaurant_gst_number")}>reset</button>}
              </div>
              <GstinAutoParse value={draft.restaurant_gst_number ?? data.effective.restaurant_gst_number ?? ""} />
              <InheritedNote k="restaurant_gst_number" />
            </FieldRow>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldRow label="Default GST rate (%)">
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="0.5"
                    value={draft.default_tax_rate ?? data.effective.default_tax_rate ?? 12}
                    onChange={(e) => setField("default_tax_rate", Number(e.target.value))}
                    data-testid="prop-override-tax-rate"
                  />
                  <OverrideBadge k="default_tax_rate" />
                  {isOverridden("default_tax_rate") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("default_tax_rate")}>reset</button>}
                </div>
                <InheritedNote k="default_tax_rate" />
              </FieldRow>

              <FieldRow label="Default GST mode">
                <div className="flex items-center gap-2">
                  <Select
                    value={String(draft.default_tax_inclusive ?? data.effective.default_tax_inclusive ?? false)}
                    onValueChange={(v) => setField("default_tax_inclusive", v === "true")}
                  >
                    <SelectTrigger data-testid="prop-override-tax-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">Exclusive of GST</SelectItem>
                      <SelectItem value="true">Inclusive of GST</SelectItem>
                    </SelectContent>
                  </Select>
                  <OverrideBadge k="default_tax_inclusive" />
                  {isOverridden("default_tax_inclusive") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("default_tax_inclusive")}>reset</button>}
                </div>
                <InheritedNote k="default_tax_inclusive" formatter={(v) => v ? "Inclusive" : "Exclusive"} />
              </FieldRow>
            </div>
          </Card>
          )}

          {/* Invoice template */}
          {show("invoice") && (
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ReceiptIcon size={18} weight="duotone" className="text-primary" />
              <h4 className="font-display text-xl">Invoice template</h4>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Prefix, footer and terms shown on invoices raised for <strong>{activeProperty?.name || "this property"}</strong>. Invoice numbering restarts per property + per year, so you can safely brand each hotel independently.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldRow label="Invoice prefix">
                <div className="flex items-center gap-2">
                  <Input
                    value={draft.invoice_prefix ?? data.effective.invoice_prefix ?? "INV"}
                    onChange={(e) => setField("invoice_prefix", e.target.value)}
                    placeholder={inherited.invoice_prefix || "INV"}
                    data-testid="prop-override-invoice-prefix"
                  />
                  <OverrideBadge k="invoice_prefix" />
                  {isOverriddenFn("invoice_prefix") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("invoice_prefix")}>reset</button>}
                </div>
                <InheritedNote k="invoice_prefix" />
              </FieldRow>

              <FieldRow label="Currency symbol">
                <div className="flex items-center gap-2">
                  <Input
                    value={draft.currency_symbol ?? data.effective.currency_symbol ?? "₹"}
                    onChange={(e) => setField("currency_symbol", e.target.value)}
                    placeholder={inherited.currency_symbol || "₹"}
                    data-testid="prop-override-currency"
                  />
                  <OverrideBadge k="currency_symbol" />
                  {isOverriddenFn("currency_symbol") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("currency_symbol")}>reset</button>}
                </div>
                <InheritedNote k="currency_symbol" />
              </FieldRow>
            </div>

            <FieldRow label="Restaurant / POS name (optional)">
              <div className="flex items-center gap-2">
                <Input
                  value={draft.restaurant_name ?? data.effective.restaurant_name ?? ""}
                  onChange={(e) => setField("restaurant_name", e.target.value)}
                  placeholder={inherited.restaurant_name || "Leave blank to reuse group name"}
                  data-testid="prop-override-restaurant-name"
                />
                <OverrideBadge k="restaurant_name" />
                {isOverriddenFn("restaurant_name") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("restaurant_name")}>reset</button>}
              </div>
              <InheritedNote k="restaurant_name" />
            </FieldRow>

            <FieldRow label="Invoice footer">
              <div className="flex items-center gap-2">
                <Input
                  value={draft.invoice_footer ?? data.effective.invoice_footer ?? ""}
                  onChange={(e) => setField("invoice_footer", e.target.value)}
                  placeholder={inherited.invoice_footer || "Thank you for staying with us."}
                  data-testid="prop-override-invoice-footer"
                />
                <OverrideBadge k="invoice_footer" />
                {isOverriddenFn("invoice_footer") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("invoice_footer")}>reset</button>}
              </div>
              <InheritedNote k="invoice_footer" />
            </FieldRow>

            <FieldRow label="Terms &amp; conditions">
              <div className="flex items-start gap-2">
                <textarea
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  value={draft.invoice_terms ?? data.effective.invoice_terms ?? ""}
                  onChange={(e) => setField("invoice_terms", e.target.value)}
                  placeholder={inherited.invoice_terms || "Payment due on check-out. GST as per government norms."}
                  data-testid="prop-override-invoice-terms"
                />
                <OverrideBadge k="invoice_terms" />
                {isOverriddenFn("invoice_terms") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("invoice_terms")}>reset</button>}
              </div>
              <InheritedNote k="invoice_terms" />
            </FieldRow>

            <div className="mt-2 rounded-md border border-border/60 p-4" data-testid="prop-override-invoice-layout">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="label-overline">Invoice layout</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Choose what prints on every invoice for this property. Unticked items are hidden on both the PDF and browser preview.</p>
                </div>
                <OverrideBadge k="invoice_layout" />
                {isOverriddenFn("invoice_layout") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("invoice_layout")}>reset</button>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mt-2">
                {INVOICE_LAYOUT_FIELDS.map(([key, label]) => {
                  const effective = { ...(inherited.invoice_layout || {}), ...(draft.invoice_layout || data.effective.invoice_layout || {}) };
                  const checked = effective[key] !== false;
                  return (
                    <label key={key} className="flex items-center gap-2 cursor-pointer rounded-md border border-border/60 px-2 py-1.5" data-testid={`prop-override-layout-${key}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setField("invoice_layout", { ...(draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}), [key]: e.target.checked })}
                        className="accent-primary"
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-3">
                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Additional footer note (optional)</label>
                <textarea
                  rows={2}
                  className="mt-1 flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={(draft.invoice_layout || data.effective.invoice_layout || {}).custom_footer_html || ""}
                  onChange={(e) => setField("invoice_layout", { ...(draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}), custom_footer_html: e.target.value })}
                  placeholder="e.g. HSN 996311 · Bank details for NEFT · SAC 9963 …"
                  data-testid="prop-override-layout-custom-footer"
                />
              </div>

              <div className="mt-4 pt-4 border-t border-border/60" data-testid="prop-override-invoice-header">
                <p className="label-overline mb-1">Header banner</p>
                <p className="text-xs text-muted-foreground mb-3">Big branded banner at the top of every invoice (like the dashboard hero). Turn off for a plain header.</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={((draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}).show_header_banner) !== false}
                    onChange={(e) => setField("invoice_layout", { ...(draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}), show_header_banner: e.target.checked })}
                    className="accent-primary"
                    data-testid="prop-override-header-banner-toggle"
                  />
                  <span>Show branded banner</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {[
                    ["header_group_overline", "Group overline (top line)", "e.g. The King's Grand Group of Hotels"],
                    ["header_property_display", "Property display name", "e.g. The Mansarovar Hotel & Banquet"],
                    ["header_location_badge", "Location badge", "e.g. GANGTOK, SIKKIM"],
                  ].map(([k, label, ph]) => (
                    <div key={k}>
                      <label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</label>
                      <input
                        type="text"
                        className="mt-1 flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        value={(draft.invoice_layout || data.effective.invoice_layout || {})[k] || ""}
                        onChange={(e) => setField("invoice_layout", { ...(draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}), [k]: e.target.value })}
                        placeholder={ph}
                        data-testid={`prop-override-header-${k}`}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Banner background</label>
                    <input
                      type="color"
                      className="mt-1 w-full h-9 rounded-md border border-input bg-transparent"
                      value={(draft.invoice_layout || data.effective.invoice_layout || {}).header_bg_color || "#2F5D50"}
                      onChange={(e) => setField("invoice_layout", { ...(draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}), header_bg_color: e.target.value })}
                      data-testid="prop-override-header-bg"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Accent (gold)</label>
                    <input
                      type="color"
                      className="mt-1 w-full h-9 rounded-md border border-input bg-transparent"
                      value={(draft.invoice_layout || data.effective.invoice_layout || {}).header_accent_color || "#C8A24B"}
                      onChange={(e) => setField("invoice_layout", { ...(draft.invoice_layout || data.effective.invoice_layout || inherited.invoice_layout || {}), header_accent_color: e.target.value })}
                      data-testid="prop-override-header-accent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
          )}

          {/* Meal plans */}
          {show("meal_plans") && (
          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ForkKnifeIcon size={18} weight="duotone" className="text-primary" />
                <h4 className="font-display text-xl">Meal plans &amp; extras</h4>
              </div>
              <OverrideBadge k="meal_plans" />
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Prices below apply when guests book with this property. Change a row and hit Save to override.
              Use &apos;reset&apos; to fall back to the platform default plan pricing.
            </p>

            <table className="w-full text-sm" data-testid="prop-override-meal-plans">
              <thead className="border-b border-border">
                <tr className="text-left"><th className="p-2">Code</th><th className="p-2">Name</th><th className="p-2 text-right">Per-person surcharge (₹)</th></tr>
              </thead>
              <tbody>
                {(currentPlans || []).map((p, i) => (
                  <tr key={p.code || i} className="border-b border-border/50 last:border-b-0">
                    <td className="p-2 font-mono-plex">{p.code}</td>
                    <td className="p-2">{p.name}</td>
                    <td className="p-2 text-right">
                      <Input
                        type="number" step="10"
                        value={p.surcharge_per_person ?? 0}
                        onChange={(e) => updatePlanRow(i, "surcharge_per_person", Number(e.target.value))}
                        className="w-28 ml-auto text-right"
                        data-testid={`prop-override-plan-${p.code}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isOverridden("meal_plans") && (
              <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("meal_plans")}>reset meal plans to platform default</button>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <FieldRow label="Extra bed rate (₹ per night)">
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="50"
                    value={draft.extra_bed_rate ?? data.effective.extra_bed_rate ?? 0}
                    onChange={(e) => setField("extra_bed_rate", Number(e.target.value))}
                    data-testid="prop-override-extra-bed"
                  />
                  <OverrideBadge k="extra_bed_rate" />
                  {isOverridden("extra_bed_rate") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("extra_bed_rate")}>reset</button>}
                </div>
                <InheritedNote k="extra_bed_rate" />
              </FieldRow>

              <FieldRow label="Child (CNB) rate (₹ per night)">
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="50"
                    value={draft.cnb_rate ?? data.effective.cnb_rate ?? 0}
                    onChange={(e) => setField("cnb_rate", Number(e.target.value))}
                    data-testid="prop-override-cnb"
                  />
                  <OverrideBadge k="cnb_rate" />
                  {isOverridden("cnb_rate") && <button type="button" className="text-[10px] text-muted-foreground underline" onClick={() => clearField("cnb_rate")}>reset</button>}
                </div>
                <InheritedNote k="cnb_rate" />
              </FieldRow>
            </div>
          </Card>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={load} disabled={saving}>Discard changes</Button>
            <Button onClick={save} disabled={saving} data-testid="prop-overrides-save">
              {saving ? "Saving…" : `Save overrides for ${activeProperty?.name || "property"}`}
            </Button>
          </div>

          <Card className="p-4 flex gap-3 items-start bg-muted/40 border-border">
            <InfoIcon size={16} className="text-muted-foreground mt-0.5" />
            <p className="text-xs text-muted-foreground">
              These overrides apply to any invoice / reservation / POS receipt attached to this property.
              Restaurant tables (POS · Dine-in) and channel-manager credentials already live per-property under their own tabs.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function GstinAutoParse({ value }) {
  if (!value) return null;
  const p = parseGSTIN(value);
  if (!p.formatted) return null;
  const ok = p.valid;
  const partial = !ok && p.state && p.pan;
  return (
    <div className={`mt-1.5 flex flex-wrap items-center gap-2 text-[11px] rounded-md border px-2 py-1.5 ${ok ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/8" : partial ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/8" : "border-destructive/40 bg-destructive/8"}`} data-testid="gstin-autoparse">
      {ok ? <CheckCircleIcon size={12} weight="fill" className="text-[hsl(var(--success))]" /> : <WarningCircleIcon size={12} weight="fill" className={partial ? "text-[hsl(var(--warning))]" : "text-destructive"} />}
      {p.state ? (
        <>
          <span className="text-muted-foreground uppercase tracking-wider">State</span>
          <strong className="text-foreground" data-testid="gstin-state">{p.state}</strong>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground uppercase tracking-wider">PAN</span>
          <strong className="font-mono-plex text-foreground" data-testid="gstin-pan">{p.pan}</strong>
          <span className="text-muted-foreground">·</span>
          <span className={ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}>{p.reason}</span>
        </>
      ) : (
        <span className="text-muted-foreground">GSTIN parse · {p.reason}</span>
      )}
    </div>
  );
}
